<template>
  <div v-for="upload in uploads" :key="upload._id" class="pt-2.5">
    <div class="flex items-center gap-2">
      <div class="flex size-[30px] shrink-0 items-center justify-center">
        <CircularProgressIndicator
          v-if="isInProgress(upload)"
          :percentage="upload.percentage"
          stroke-color="#00B3E6"
          class="border-gray-200/30!"
        />
        <CheckedSolidIcon
          v-else-if="hasSucceeded(upload)"
          class="size-[26px]"
        />
        <div
          v-else
          class="flex size-[26px] items-center justify-center rounded-full bg-red-200/50"
        >
          <AlertIcon class="size-4 text-red-600" />
        </div>
      </div>

      <div class="flex min-w-0 flex-1 items-center justify-between gap-3">
        <div class="min-w-0 flex-1">
          <div
            v-truncate
            class="w-full text-[14px] leading-[18px] text-gray-800"
          >
            <div data-truncate-target>{{ upload.fileName }}</div>
          </div>
          <div
            v-if="statusLabel(upload)"
            class="text-[14px] leading-[18px]"
            :class="isErrored(upload) ? 'text-red-600' : 'text-gray-500'"
          >
            {{ statusLabel(upload) }}
          </div>
        </div>

        <Button
          v-if="isInProgress(upload)"
          variant="stroke"
          size="md"
          class="shrink-0"
          @click="handleCancel(upload)"
        >
          Cancel
        </Button>
      </div>
    </div>

    <div v-if="hasFailed(upload)" class="ml-[38px] mt-2.5">
      <Alert variant="error">
        {{ getError(upload.error!) }}
      </Alert>
    </div>

    <div class="ml-[38px] mt-2.5 h-px bg-[#efefef]" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Button, Alert } from '@dacr/library'
import { CheckedSolidIcon, AlertIcon } from '@dacr/icons'
import CircularProgressIndicator from '@/shared/components/CircularProgressIndicator.vue'
import { useUploadStore, type Upload } from '@/features/upload/store'
import { ErrorCodes } from '@/shared/graphql/legacy/generated'
import { formatTimeLeft } from '@/shared/utils/datetime'
import { openModal, Modals } from '@/shared/dialogs/modals'

const uploadStore = useUploadStore()

const uploads = computed(() => uploadStore.uploads)

const hasSucceeded = (upload: Upload) =>
  upload.percentage === 100 && !upload.error

const isErrored = (upload: Upload) => !!upload.error

const isInProgress = (upload: Upload) =>
  !upload.error && upload.percentage < 100

const hasFailed = (upload: Upload) => !!upload.error && !upload.canceled

const statusLabel = (upload: Upload) => {
  if (upload.canceled) return 'Upload cancelled'
  if (upload.error) return 'Upload error'
  if (isInProgress(upload) && upload.timeLeft) {
    return formatTimeLeft(upload.timeLeft)
  }
  return ''
}

const getError = (error: ErrorCodes) => {
  if (
    error === ErrorCodes.PaymentFailed ||
    error === ErrorCodes.PaymentMethodInvalid ||
    error === ErrorCodes.PaymentActionRequired
  ) {
    return 'There is an issue with your payment method. Please fix your payment to continue using Dacr.'
  }

  if (error === ErrorCodes.LimitExceeded) {
    return 'You have exceeded the limit of files you can upload. Please upgrade your plan to continue using Dacr.'
  }

  if (error === ErrorCodes.UploadFileSizeExceededError) {
    return 'File size is too large. The maximum file size that you can add to Dacr is 2GB. Please compress the file and try again.'
  }

  return 'Upload failed due to a technical issue. Please try again or contact us.'
}

const handleCancel = (upload: Upload) => {
  openModal(Modals.CancelUpload, { stack: true, props: { upload } })
}
</script>
